import { useEffect, useState } from "react";
import { Input, DatePicker, message, Spin, Empty, ConfigProvider, Image } from "antd";
import dayjs from "dayjs";
import Header from "../../../component/menu/Header";
import SideMenu from "../../../component/menu/SideMenu";
import { constants } from "../../../constant/constants";
import InventoryTable from "../../../component/inventory/InventoryTable";
import ApiService from "../../../utils/ApiService";
import useUserStore from "../../../stores/store";
import {
  getReportsCurrentPageTotal,
  prepareInventoryReport,
} from "../../../services/inventory/inventory-report.service";
import { downloadPDF } from "../../../services/inventory/inventory.service";
import CustomLoader from "../../../component/menu/CustomLoader";

export default function InventoryReport() {
  const { user, restaurantId, showNotification, setNetworkCall } = useUserStore();
  const [searchText, setSearchText] = useState("");
  const [fromDate, setFromDate] = useState(new Date().toLocaleDateString("en-CA"));
  const [toDate, setToDate] = useState(null);
  // const [status, setStatus] = useState();
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [reportLoading, setReportLoading] = useState(true);
  const [inventoryReport, setInventoryReport] = useState();
  const [reportsTotal, setReportsTotal] = useState(0);

  function onHandlePageChange(page, pageSize) {
    setPage(page);
    setPerPage(pageSize);
    const tempFilter = {
      restaurantId,
      searchKey: searchText.trim(),
      fromDate: fromDate || "",
      toDate: toDate || "",
      page,
      perPage: pageSize,
      // status: status,
    };

    getInventoryReport(tempFilter);
  }

  function onHandleSearchText(e) {
    const val = e.target.value;
    setSearchText(val);
  }

  function onHandleFromDate(date, dateString) {
    if (!Boolean(date) || dateString === "") {
      setFromDate(null);
      return;
    }

    if (dateString != "" && toDate && !(new Date(toDate) >= new Date(dateString))) {
      message.warning(
        <p className="font-p_regular text-[12px]">
          From date must be less than or equal to <span className="text-red-500">to date</span>.
        </p>
      );
      return;
    }

    setFromDate(dateString);
  }

  function onHandleToDate(date, dateString) {
    if (!Boolean(date) || dateString === "") {
      setToDate(null);
      return;
    }
    if (dateString != "" && fromDate && !(new Date(fromDate) <= new Date(dateString))) {
      message.warning(
        <p className="font-p_regular text-[12px]">
          To date must be grater than or equal to <span className="text-red-500">from date</span>.
        </p>
      );
      return;
    }
    setToDate(dateString);
  }

  function onHandleSearch() {
    if (searchText === "" && fromDate === null && toDate === null) {
      message.warning(
        <p className="font-p_regular text-[12px]">
          Please enter search <span className="text-red-500">criteria.</span>
        </p>
      );
      return;
    }
    const tempFilter = {
      restaurantId,
      searchKey: searchText.trim(),
      fromDate: fromDate || "",
      toDate: toDate || "",
      // status: status,
    };

    getInventoryReport(tempFilter);
  }

  // function onHandleStatus(val) {
  //   setStatus(val);
  // }

  function clearFilterFields() {
    setSearchText("");
    setFromDate(new Date().toLocaleDateString("en-CA"));
    setToDate(null);
    // setStatus("");
  }

  function onHandleClear() {
    clearFilterFields();
    getInventoryReport({ restaurantId, fromDate: new Date().toLocaleDateString("en-CA") });
  }

  async function getInventoryReport(params) {
    setReportLoading(true);
    if (!params) return;
    if (!params.hasOwnProperty("restaurantId")) {
      console.error("restaurantId is required for getting ingredient list.");
      return;
    }
    try {
      const res = await ApiService.GET_INVENTORY_REPORT(params, user.token.token);
      if (res.code !== 200) {
        showNotification(res.data.message);
        setReportLoading(false);
        return;
      }
      const { payload } = res;
      const { page, perPage, total, records } = payload;
      setPage(page);
      setPerPage(perPage);
      setTotal(total);
      setReportsTotal(getReportsCurrentPageTotal(records));
      setInventoryReport(prepareInventoryReport(records));
      setReportLoading(false);
    } catch (err) {
      setReportLoading(false);
      showNotification(err.response.data.message);
      return;
    }
  }

  async function downloadStockData(params) {
    try {
      setNetworkCall(true);
      const res = await ApiService.GET_DOWNLOAD_STOCK_REPORT(params, user.token.token);
      if (Boolean(res) && res.status !== 200) {
        console.error(res);
        showNotification(res.message);
        setNetworkCall(false);
        return;
      }
      setNetworkCall(false);
      downloadPDF(res.data, "stock-report.pdf");
      showNotification("Stock report downloads successfully!", false);
    } catch (err) {
      setNetworkCall(false);
      console.log("Error in stock report. Error", err);
      showNotification(err?.response?.data?.message);
      return;
    }
  }

  function onHandleDownloadReport() {
    const params = {
      restaurantId,
      searchKey: searchText,
      fromDate: fromDate || "",
      toDate: toDate || ""
    }
    downloadStockData(params);
  }

  useEffect(() => {
    getInventoryReport({ restaurantId, fromDate: fromDate || "" });
  }, [restaurantId]);

  return (
    <div className="flex flex-col">
      <CustomLoader />
      <Header title={`McDonalds Food Chains`} menu={constants.MENU.INVENTORY} currentMenuKey={"T_7_S_7_L_4"} />
      <div className="flex flex-row bg-prewhite">
        <SideMenu title={`McDonalds Food Chain`} menu={constants.MENU.INVENTORY} />
        <div className="flex flex-row p-[24px] w-full mt-[80px] ml-[72px]">
          <div className="flex flex-col w-full">
            <h1 className="pt-[26px] pb-[20px] font-bold text-black text-[24px] leading-[24px] cursor-default">
              Stock
            </h1>
            {/* filter section */}
            <ConfigProvider
              theme={{
                components: {
                  DatePicker: {
                    zIndexPopup: 1,
                  },
                },
              }}
            >
              <div className="mb-[20px] flex items-center justify-between">
                <div className="flex items-center space-x-[20px] w-full">
                  <div className="min-w-[332px] h-[48px]">
                    <Input
                      name="search"
                      className={"w-full h-full"}
                      value={searchText}
                      onChange={onHandleSearchText}
                      onPressEnter={onHandleSearch}
                      placeholder="Search keyword"
                    />
                  </div>

                  <div className="min-w-[258px] h-[48px]">
                    <DatePicker
                      className={"h-full w-full"}
                      showTime={false}
                      onChange={onHandleFromDate}
                      placeholder="From Date"
                      value={fromDate ? dayjs(fromDate) : null}
                      disabledDate={(current) => current && current > dayjs().endOf("day")}
                    />
                  </div>
                  <div className="min-w-[256px] h-[48px]">
                    <DatePicker
                      className={"h-full w-full"}
                      showTime={false}
                      onChange={onHandleToDate}
                      placeholder="To Date"
                      value={toDate ? dayjs(toDate) : null}
                      disabledDate={(current) => current && current > dayjs().endOf("day")}
                    />
                  </div>

                  <div className="flex items-center space-x-3">
                    <button
                      className="w-[100px] h-[48px] bg-black rounded-[5px] text-white font-bold leading-[16px]"
                      onClick={onHandleClear}
                    >
                      Clear
                    </button>
                    <button
                      className="w-[100px] h-[48px] bg-[#FCBD10] rounded-[5px] text-black font-bold leading-[16px]"
                      onClick={onHandleSearch}
                    >
                      Search
                    </button>
                  </div>
                </div>

                <div className="w-[56px] h-[56px] flex items-center justify-center rounded-full bg-[#FCBD10]">
                  <button className="w-full h-full " onClick={onHandleDownloadReport}>
                    <Image src="/images/download-all.svg" preview={false} height={48} width={48} />
                  </button>
                </div>
              </div>
            </ConfigProvider>
            {/* filter section ends */}

            <div>
              {reportLoading ? (
                <div className="h-[50vh] flex items-center justify-center">
                  <Spin size="large" />
                </div>
              ) : Array.isArray(inventoryReport) && inventoryReport.length < 1 ? (
                <div className="h-[50vh] flex items-center justify-center">
                  <Empty description={"No Data Found!"} />
                </div>
              ) : (
                <div>
                  <InventoryTable
                    data={inventoryReport}
                    coloredCols={["SN", "Item Name", "Added Quantity", "Wastage", "Closing Stocks", "Purchase Date"]}
                    showActionButtons={false}
                    showBottomTotal={true}
                    bottomTotalValue={reportsTotal}
                    paginationRecords={{
                      handlePageChange: onHandlePageChange,
                      page: page,
                      perPage: perPage,
                      setPerPage: setPerPage,
                      total: total,
                    }}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
